/**
 * client/render/post.ts — final pass: map colour-grading LUT (mp_hijacked_lut) on the sqrt-encoded
 * scene. OWNER: RENDER agent.
 *
 * The world/viewmodel are rendered into an MSAA 8-bit target holding `sqrt(col * exposure)` (the value the
 * BO2 material shaders output, research/map-hijacked.md). The engine then applies the map LUT
 * (techniqueSet hdr_create_lut2dv: a 32^3 LUT built from the 1024x32 strip). Which of the two stacked LUTs
 * of mp_hijacked_lut_win the engine binds is unknown -> top one (ESTIMATE, lut-data.ts).
 */
import * as THREE from 'three';
import { LUT_RGB_B64, LUT_SIZE } from './lut-data';

function decodeB64(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function createLutTexture(): THREE.Data3DTexture {
  const rgb = decodeB64(LUT_RGB_B64);
  const n = LUT_SIZE;
  const rgba = new Uint8Array(n * n * n * 4);
  for (let i = 0, j = 0; i < n * n * n; i++, j += 3) {
    rgba[i * 4] = rgb[j]; rgba[i * 4 + 1] = rgb[j + 1]; rgba[i * 4 + 2] = rgb[j + 2]; rgba[i * 4 + 3] = 255;
  }
  const t = new THREE.Data3DTexture(rgba, n, n, n);
  t.format = THREE.RGBAFormat;
  t.type = THREE.UnsignedByteType;
  t.minFilter = t.magFilter = THREE.LinearFilter;
  t.wrapS = t.wrapT = t.wrapR = THREE.ClampToEdgeWrapping;
  t.unpackAlignment = 1;
  t.colorSpace = THREE.NoColorSpace;
  t.needsUpdate = true;
  return t;
}

const POST_VERT = /* glsl */ `
out vec2 vUv;
void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;
const POST_FRAG = /* glsl */ `
precision highp float;
precision highp sampler3D;
uniform sampler2D tScene;
uniform sampler3D tLut;
uniform float uLutMix;
uniform float uFlash;
in vec2 vUv;
void main() {
  vec3 c = texture(tScene, vUv).rgb;
  // lut index = (r, g, b) -> texel (x = r, y = g, z = b)
  vec3 lc = clamp(c, 0.0, 1.0) * (31.0 / 32.0) + 0.5 / 32.0;
  vec3 g = texture(tLut, lc).rgb;
  vec3 o = mix(c, g, uLutMix);
  o += vec3(uFlash);
  gl_FragColor = vec4(o, 1.0);
}
`;

export class PostPass {
  readonly target: THREE.WebGLRenderTarget;
  private readonly scene = new THREE.Scene();
  private readonly cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  readonly material: THREE.ShaderMaterial;

  constructor(samples: number) {
    this.target = new THREE.WebGLRenderTarget(4, 4, {
      samples,
      type: THREE.UnsignedByteType,
      format: THREE.RGBAFormat,
      depthBuffer: true,
      stencilBuffer: false,
      // Plain RGBA8 holding the sqrt-encoded values our ShaderMaterials write (no hardware sRGB). The
      // viewmodel pass (three built-in materials) is drawn after the post pass, straight to the canvas.
      colorSpace: THREE.NoColorSpace,
    });
    this.target.texture.generateMipmaps = false;
    this.material = new THREE.ShaderMaterial({
      uniforms: { tScene: { value: this.target.texture }, tLut: { value: createLutTexture() }, uLutMix: { value: 1 }, uFlash: { value: 0 } },
      vertexShader: POST_VERT,
      fragmentShader: POST_FRAG,
      depthTest: false,
      depthWrite: false,
    });
    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.material);
    quad.frustumCulled = false;
    this.scene.add(quad);
  }

  setSize(w: number, h: number): void { this.target.setSize(w, h); }

  render(r: THREE.WebGLRenderer): void {
    r.setRenderTarget(null);
    r.render(this.scene, this.cam);
  }

  dispose(): void {
    this.target.dispose();
    this.material.dispose();
  }
}
